import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const EqualizerBar = styled('div')(({ theme }) => ({
  width: 8,
  backgroundColor: '#4CAF50', // Material-UI green
  marginRight: 4,
  borderRadius: 4,
  transition: 'height 0.05s ease'
}));

const RecordingIndicator = () => {
  const [audioData, setAudioData] = useState(new Array(16).fill(2));
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    let mediaStream = null;

    const initializeAudio = async () => {
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ 
          audio: { 
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false
          } 
        });
        
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContextRef.current.createMediaStreamSource(mediaStream);
        analyserRef.current = audioContextRef.current.createAnalyser();
        
        analyserRef.current.fftSize = 256;
        analyserRef.current.minDecibels = -85;
        analyserRef.current.maxDecibels = -10;
        analyserRef.current.smoothingTimeConstant = 0.7;

        source.connect(analyserRef.current);

        const updateEqualizer = () => {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(dataArray);
          
          const voiceRange = dataArray.slice(2, 18);
          
          const barHeights = Array.from(voiceRange).map(value => {
            const normalized = value / 255;
            const scaled = Math.pow(normalized, 0.8) * 60;
            return Math.max(2, Math.min(60, scaled));
          });
          
          setAudioData(barHeights);
          animationFrameRef.current = requestAnimationFrame(updateEqualizer);
        };

        updateEqualizer();
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    };

    initializeAudio();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <Box sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      padding: '12px 20px',
      borderRadius: '6px',
      zIndex: 1
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        height: 60,
        mb: 1.5,
        minWidth: '200px',  // Added to ensure minimum width
        justifyContent: 'center'
      }}>
        {audioData.map((height, index) => (
          <EqualizerBar
            key={index}
            sx={{ height: `${height}px` }}
          />
        ))}
      </Box>
      <Typography variant="body2" color="white">
        Recording...
      </Typography>
    </Box>
  );
};

export default RecordingIndicator;